/**
 * CacheStorageRepository - Cache API-based persistent storage (Browser Bundle Version)
 * Implements IAsyncRepository for asynchronous access to Cache API
 *
 * This provides Layer 3 caching in the handler chain:
 * 1. Memory (sync) → 2. LocalStorage (sync) → 3. CacheStorage (async) → 4. Network (async)
 */

import type { IAsyncRepository } from '../../interfaces/repository.interface.js';
import type { StorageRepositoryConfig } from '../../config/repository.config.js';
import type { TranslationData } from '../../types/translation-data.types.js';
import { TranslationNotFoundError } from '../../errors/translation-errors.js';
import { CORE_CACHE_NAMES, CORE_CACHE_ENTRY_PREFIX } from './cache-names.js';

// Single source of truth for both names — see cache-names.ts. These were
// module-private literals duplicated by @lionrapid/storage's Service Worker,
// which is how the SW came to delete this repository's store.
const CACHE_NAME = CORE_CACHE_NAMES.translations;
// The Cache API only accepts http/https request URLs as keys — `cache.put`
// throws `TypeError: Request scheme '…' is unsupported` for a bare string like
// `lionrapid:…`, which silently killed every write (caught by set()'s guard).
// Use a synthetic https origin so keys are valid Requests. The host never
// resolves; it only namespaces entries inside the named cache.
const CACHE_PREFIX = CORE_CACHE_ENTRY_PREFIX;

/**
 * Stored item structure with TTL support
 */
interface StoredItem {
  value: string;
  expiry: number | null;
  timestamp: number;
}

/**
 * Configuration options for CacheStorageRepository
 */
export interface CacheStorageRepositoryConfig extends StorageRepositoryConfig {
  options?: StorageRepositoryConfig['options'] & {
    /** Cache name for the Cache API (default: 'lionrapid-v1') */
    cacheName?: string;
    /** TTL in milliseconds (default: 7 days) */
    ttl?: number;
  };
}

/**
 * Cache API-based repository for persistent translation caching
 *
 * Features:
 * - Asynchronous access (Cache API is async)
 * - TTL-based expiration
 * - Larger storage capacity than localStorage
 * - Graceful fallback when Cache API unavailable (SSR, old browsers)
 * - Works alongside Service Worker for offline support
 *
 * @example
 * ```typescript
 * import { CacheStorageRepository } from '@lionrapid/storage';
 *
 * const cacheRepo = new CacheStorageRepository({
 *   enabled: true,
 *   options: {
 *     cacheName: 'my-app-translations',
 *     ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
 *   },
 * });
 * ```
 */
export class CacheStorageRepository
  implements IAsyncRepository<CacheStorageRepositoryConfig>
{
  readonly name = 'CacheStorageRepository';
  readonly type = 'storage' as const;
  readonly config: CacheStorageRepositoryConfig;

  private cacheName: string;
  private defaultTTL: number;
  private available: boolean;

  constructor(config: CacheStorageRepositoryConfig = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      priority: config.priority ?? 0,
      options: {
        cacheName: config.options?.cacheName ?? CACHE_NAME,
        ttl: config.options?.ttl ?? 7 * 24 * 60 * 60 * 1000, // 7 days default
        ...config.options,
      },
    };

    this.cacheName = this.config.options?.cacheName ?? CACHE_NAME;
    this.defaultTTL = this.config.options?.ttl ?? 7 * 24 * 60 * 60 * 1000;
    this.available = this.checkAvailability();
  }

  /**
   * Check if Cache API is available
   * Returns false in SSR, old browsers, or insecure contexts
   */
  private checkAvailability(): boolean {
    return typeof caches !== 'undefined';
  }

  /**
   * Get a translation value by key
   * @throws {TranslationNotFoundError} When translation doesn't exist or is expired
   */
  async get(key: string): Promise<string> {
    if (!this.available) {
      throw new TranslationNotFoundError(key);
    }

    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(CACHE_PREFIX + key);

      if (!response) {
        throw new TranslationNotFoundError(key);
      }

      const data: StoredItem = await response.json();

      // Check TTL expiry
      if (data.expiry !== null && Date.now() > data.expiry) {
        await cache.delete(CACHE_PREFIX + key);
        throw new TranslationNotFoundError(key);
      }

      return data.value;
    } catch (error) {
      if (error instanceof TranslationNotFoundError) {
        throw error;
      }
      // Wrap any other error as not found
      throw new TranslationNotFoundError(key);
    }
  }

  /**
   * Set a translation value
   */
  async set(key: string, value: string): Promise<void> {
    if (!this.available) return;

    try {
      const cache = await caches.open(this.cacheName);
      const data: StoredItem = {
        value,
        expiry: this.defaultTTL ? Date.now() + this.defaultTTL : null,
        timestamp: Date.now(),
      };

      await cache.put(
        CACHE_PREFIX + key,
        new Response(JSON.stringify(data), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
    } catch {
      // Silently fail - cache is non-critical
    }
  }

  /**
   * Remove a translation by key
   */
  async remove(key: string): Promise<void> {
    if (!this.available) return;

    try {
      const cache = await caches.open(this.cacheName);
      await cache.delete(CACHE_PREFIX + key);
    } catch {
      // Silently fail
    }
  }

  /**
   * Clear all LionRapid entries from the cache
   */
  async clear(): Promise<void> {
    if (!this.available) return;

    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      await Promise.all(
        keys
          .filter(request => request.url.includes(CACHE_PREFIX))
          .map(request => cache.delete(request))
      );
    } catch {
      // Silently fail
    }
  }

  /**
   * Clear all entries for a specific namespace
   */
  async clearNamespace(locale: string, namespace: string): Promise<void> {
    if (!this.available) return;

    const namespaceCacheKey = `@${locale}:${namespace}`;
    const keyPrefix = `${locale}:${namespace}:`;

    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      await Promise.all(
        keys
          .filter(request => {
            const url = request.url;
            const key = url.includes(CACHE_PREFIX)
              ? url.substring(url.indexOf(CACHE_PREFIX) + CACHE_PREFIX.length)
              : '';
            return key === namespaceCacheKey || key.startsWith(keyPrefix);
          })
          .map(request => cache.delete(request))
      );
    } catch {
      // Silently fail
    }
  }

  /**
   * Get namespace translations
   * @throws {TranslationNotFoundError} When namespace doesn't exist
   */
  async getNamespace(
    locale: string,
    namespace: string
  ): Promise<TranslationData> {
    const namespaceCacheKey = `@${locale}:${namespace}`;
    const value = await this.get(namespaceCacheKey);

    try {
      return JSON.parse(value);
    } catch {
      await this.remove(namespaceCacheKey);
      throw new TranslationNotFoundError(namespaceCacheKey);
    }
  }

  /**
   * Set namespace translations
   */
  async setNamespace(
    locale: string,
    namespace: string,
    translations: TranslationData
  ): Promise<void> {
    const namespaceCacheKey = `@${locale}:${namespace}`;
    await this.set(namespaceCacheKey, JSON.stringify(translations));
  }

  /**
   * Get repository configuration
   */
  getConfig(): CacheStorageRepositoryConfig {
    return this.config;
  }

  /**
   * Check if Cache API is available
   */
  isAvailable(): boolean {
    return this.available;
  }

  /**
   * Get all cached entries (for debugging)
   * Returns an object mapping keys to values
   */
  async getEntries(): Promise<Record<string, string>> {
    const entries: Record<string, string> = {};
    if (!this.available) return entries;

    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();

      for (const request of keys) {
        const url = request.url;
        if (!url.includes(CACHE_PREFIX)) continue;

        const key = url.substring(
          url.indexOf(CACHE_PREFIX) + CACHE_PREFIX.length
        );
        try {
          const value = await this.get(key);
          entries[key] = value;
        } catch {
          // Skip expired or invalid entries
        }
      }
    } catch {
      // Silently fail
    }

    return entries;
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{ size: number; cacheName: string }> {
    if (!this.available) {
      return { size: 0, cacheName: this.cacheName };
    }

    try {
      const cache = await caches.open(this.cacheName);
      const keys = await cache.keys();
      const size = keys.filter(r => r.url.includes(CACHE_PREFIX)).length;

      return { size, cacheName: this.cacheName };
    } catch {
      return { size: 0, cacheName: this.cacheName };
    }
  }
}
